<template>
  <teleport to="body">
    <WdsModal
      :show="open"
      custom-size="550px"
      no-close-icon
      data-test="modal-confirm-engagement"
      @cancel="emit('close')"
    >
      <WdsStack direction-column gap-md>
        <WdsText xl primary>
          {{ isAmendment ? $t('Confirm engagement changes') : $t('Confirm engagement') }}
        </WdsText>
        <WdsText tertiary>
          {{
            isAmendment
              ? $t('Before confirming here, amend the supplier version of the hire and wait for the worker or supplier to accept the change there.')
              : $t('Review the engagement details and confirm to proceed.')
          }}
        </WdsText>

        <WdsStack v-if="hire && !loading" direction-column gap-sm class="tw-mt-2">
          <WdsStack
            v-if="hire.worker?.name"
            justify-space-between
            class="tw-border-b tw-border-gray-200 tw-pb-2"
          >
            <WdsText secondary>{{ $t('Worker') }}</WdsText>
            <WdsText>{{ hire.worker.name }}</WdsText>
          </WdsStack>

          <!-- Amendment: show the fields the client has changed -->
          <template v-if="isAmendment">
            <WdsStack
              v-for="row in changeDiff"
              :key="row.label"
              justify-space-between
              class="tw-border-b tw-border-gray-200 tw-pb-2"
              data-test="confirm-engagement-diff-row"
            >
              <WdsText secondary>{{ row.label }}</WdsText>
              <WdsText>{{ row.from }} → {{ row.to }}</WdsText>
            </WdsStack>
          </template>

          <!-- Initial offer: show the current contract details -->
          <template v-else>
            <WdsStack
              v-if="formattedRate"
              justify-space-between
              class="tw-border-b tw-border-gray-200 tw-pb-2"
            >
              <WdsText secondary>{{ $t('Rate') }}</WdsText>
              <WdsText>{{ formattedRate }}</WdsText>
            </WdsStack>

            <WdsStack
              v-if="hire.startDate"
              justify-space-between
              class="tw-border-b tw-border-gray-200 tw-pb-2"
            >
              <WdsText secondary>{{ $t('Start date') }}</WdsText>
              <WdsText>{{ formatDate(hire.startDate) }}</WdsText>
            </WdsStack>

            <WdsStack
              v-if="hire.endDate"
              justify-space-between
              class="tw-border-b tw-border-gray-200 tw-pb-2"
            >
              <WdsText secondary>{{ $t('End date') }}</WdsText>
              <WdsText>{{ formatDate(hire.endDate) }}</WdsText>
            </WdsStack>
          </template>
        </WdsStack>

        <WdsStack v-else-if="loading" justify-center class="tw-py-4">
          <WdsSpinner />
        </WdsStack>
      </WdsStack>

      <template #footer>
        <form :action="acceptUrl" method="POST" class="tw-w-full" @submit="handleSubmit">
          <input type="hidden" name="_token" :value="csrfToken" />

          <WdsStack justify-end gap-sm>
            <WdsButton type="button" :disabled="submitting" @click="emit('close')">
              {{ $t('Cancel') }}
            </WdsButton>
            <WdsButton
              type="submit"
              primary
              :disabled="submitting || loading"
              :loading="submitting"
              data-test="button-confirm-engagement"
            >
              {{ isAmendment ? $t('Confirm changes') : $t('Confirm engagement') }}
            </WdsButton>
          </WdsStack>
        </form>
      </template>
    </WdsModal>
  </teleport>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsButton,
  WdsModal,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { graphql } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import useDate from '@/composables/useDate'
import { useContractDiff } from '@/composables/useContractDiff'

const props = withDefaults(
  defineProps<{
    open?: boolean
    hireId: string
  }>(),
  {
    open: false,
  },
)

const emit = defineEmits<{
  close: []
}>()

const submitting = ref(false)

const csrfToken = document
  .querySelector('meta[name="csrf-token"]')
  ?.getAttribute('content')

const { result, loading } = useQuery(
  graphql(`
    query ConfirmEngagementDetails($id: ID!) {
      hire(id: $id) {
        id
        number
        pendingContractChanges
        worker {
          id
          name
        }
        activeContract {
          id
          rate
          rateType
          currency
          startDate
          endDate
        }
        draftContract {
          id
          rate
          rateType
          currency
          startDate
          endDate
        }
        startDate
        endDate
      }
    }
  `),
  () => ({ id: props.hireId }),
  {
    clientId: 'publicApi',
    enabled: computed(() => props.open),
  },
)

const hire = computed(() => result.value?.hire)
const isAmendment = computed(() => hire.value?.pendingContractChanges ?? false)

const acceptUrl = computed(
  () => `/hires/${hire.value?.number}/accept`,
)

const formattedRate = computed(() => {
  const contract = hire.value?.activeContract
  if (!contract?.rate) return null

  const rateType = contract.rateType?.toLowerCase() ?? ''
  return `${contract.currency ?? ''} ${contract.rate} / ${rateType}`.trim()
})

const changeDiff = useContractDiff(
  computed(() => hire.value?.activeContract ?? null),
  computed(() => hire.value?.draftContract ?? null),
)

const formatDate = (dateStr: string) => useDate(dateStr, 'MMM DD, YYYY').date.value

const handleSubmit = () => {
  submitting.value = true
}
</script>

<style scoped lang="scss">
:deep(.wds-modal-header) {
  @apply tw-p-0;
}
</style>
